import { setUiLanguage } from "../../site/ui-language.js";
const SESSION_CACHE_KEY = "zdeutsch.session.snapshot.v2";
const ACCOUNT_CACHE_PREFIX = "zdeutsch.account.snapshot.v2:";
const CATALOG_CACHE_PREFIX = "zdeutsch.catalog.snapshot.v3:";
const LAST_USER_KEY = "zdeutsch.last-user.v1";
const MAX_SESSION_AGE_MS = 30 * 60 * 1000;
const MAX_ACCOUNT_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_CATALOG_AGE_MS = 14 * 24 * 60 * 60 * 1000;

let memorySession = readEntry(window.sessionStorage, SESSION_CACHE_KEY, MAX_SESSION_AGE_MS)?.payload || null;
let sessionRequest = null;
let sessionValidatedAt = 0;
let accountRequest = null;
let accountGeneration = 0;

function readEntry(storage, key, maxAgeMs) {
  try {
    const entry = JSON.parse(storage.getItem(key) || "null");
    if (!entry || typeof entry !== "object" || entry.payload === undefined) return null;
    const savedAt = Number(entry.savedAt || 0);
    if (!savedAt || Date.now() - savedAt > maxAgeMs) {
      storage.removeItem(key);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

function writeEntry(storage, key, payload) {
  try {
    storage.setItem(key, JSON.stringify({ payload, savedAt: Date.now() }));
    return true;
  } catch {
    return false;
  }
}

function removeByPrefix(storage, prefix) {
  try {
    const keys = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // Storage can be unavailable in strict privacy modes.
  }
}

function normalizedUserId(value) {
  const id = Number(value || 0);
  return Number.isSafeInteger(id) && id > 0 ? String(id) : "";
}

function currentUserId() {
  return normalizedUserId(memorySession?.user?.id);
}

function lastCatalogUserId() {
  const liveId = currentUserId();
  if (liveId) return liveId;
  try {
    return normalizedUserId(window.localStorage.getItem(LAST_USER_KEY));
  } catch {
    return "";
  }
}

export function getCachedSession() {
  return memorySession;
}

export function cacheSession(payload) {
  memorySession = payload && typeof payload === "object" ? payload : null;
  if (!memorySession) return;
  sessionValidatedAt = Date.now();
  setUiLanguage(memorySession.user?.nativeLanguage || memorySession.uiLanguage || window.ZDeutschUiLanguage || document.documentElement.lang);
  writeEntry(window.sessionStorage, SESSION_CACHE_KEY, memorySession);
  const userId = normalizedUserId(memorySession.user?.id);
  if (userId) {
    try {
      window.localStorage.setItem(LAST_USER_KEY, userId);
    } catch {
      // Local catalog fallback remains optional.
    }
  } else {
    invalidateAccountCache();
    removeByPrefix(window.localStorage, CATALOG_CACHE_PREFIX);
    try {
      window.localStorage.removeItem(LAST_USER_KEY);
    } catch {
      // Storage cleanup is best-effort.
    }
  }
}

export function refreshSession({ force = false } = {}) {
  if (sessionRequest) return sessionRequest;
  if (!force && memorySession && Date.now() - sessionValidatedAt < 30_000) return Promise.resolve(memorySession);
  const request = fetch("/session", {
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json" }
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      cacheSession(payload);
      return payload;
    })
    .catch((error) => {
      if (memorySession) return memorySession;
      throw error;
    })
    .finally(() => {
      if (sessionRequest === request) sessionRequest = null;
    });
  sessionRequest = request;
  return request;
}

export function getCachedAccount() {
  const userId = currentUserId();
  if (!userId) return null;
  const entry = readEntry(window.sessionStorage, `${ACCOUNT_CACHE_PREFIX}${userId}`, MAX_ACCOUNT_AGE_MS);
  const payloadUserId = normalizedUserId(entry?.payload?.user?.id);
  return payloadUserId === userId && entry.payload?.user?.nativeLanguage === memorySession?.user?.nativeLanguage ? entry.payload : null;
}

export function cacheAccount(payload) {
  const userId = normalizedUserId(payload?.user?.id);
  if (!userId || (currentUserId() && currentUserId() !== userId)) return false;
  return writeEntry(window.sessionStorage, `${ACCOUNT_CACHE_PREFIX}${userId}`, payload);
}

export function refreshAccount() {
  if (accountRequest) return accountRequest;
  const generation = accountGeneration;
  const request = fetch("/account", {
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json" }
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
    if (generation === accountGeneration) cacheAccount(payload);
    return payload;
  }).finally(() => {
    if (accountRequest === request) accountRequest = null;
  });
  accountRequest = request;
  return request;
}

export function invalidateAccountCache() {
  accountGeneration += 1;
  accountRequest = null;
  removeByPrefix(window.sessionStorage, ACCOUNT_CACHE_PREFIX);
}

function catalogScope(payload) {
  if (payload?.authenticated) return lastCatalogUserId() || "";
  return "guest";
}

function catalogKey(moduleKey, level, scope = lastCatalogUserId() || "guest") {
  return `${CATALOG_CACHE_PREFIX}${scope}:${String(moduleKey || "")}:${String(level || "")}`;
}

export function getCachedCatalog(moduleKey, level) {
  const scope = lastCatalogUserId() || "guest";
  const entry = readEntry(window.localStorage, catalogKey(moduleKey, level, scope), MAX_CATALOG_AGE_MS);
  return entry?.payload || null;
}

export function cacheCatalog(moduleKey, level, payload) {
  const scope = catalogScope(payload);
  if (!scope) return false;
  return writeEntry(window.localStorage, catalogKey(moduleKey, level, scope), payload);
}

export function clearUserCaches() {
  memorySession = null;
  sessionValidatedAt = 0;
  window.ZDeutschHorenContent?.clear();
  sessionRequest = null;
  accountRequest = null;
  accountGeneration += 1;
  try {
    window.sessionStorage.removeItem(SESSION_CACHE_KEY);
    window.localStorage.removeItem(LAST_USER_KEY);
  } catch {
    // Storage cleanup is best-effort.
  }
  removeByPrefix(window.sessionStorage, ACCOUNT_CACHE_PREFIX);
  removeByPrefix(window.localStorage, CATALOG_CACHE_PREFIX);
}
